import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Car, Search, Shield, Clock, Wrench, Droplets, Settings, CheckCircle, AlertTriangle, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import brokenSideWindow from '../../assets/BrokenSideWindowSquared.jpg';

export default function SideGlass() {
	return (
		<div>
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero"
			>
				<div className="container py-12 md:py-16">
					<div className="grid md:grid-cols-2 gap-12 items-center">
						<motion.div
							initial={{ opacity: 0, x: -30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<motion.h1 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.3 }}
								className="text-3xl md:text-4xl font-extrabold text-gradient"
							>
								Side Glass Replacement
							</motion.h1>
							<motion.p 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="mt-3 max-w-3xl text-gray-600"
							>
								Your side windows, door glasses, and quarter glasses are essential components of your vehicle's safety and security system. At Ubiquity AutoGlass, we specialize in providing reliable, high-quality side glass replacement services designed to restore your vehicle's integrity and protect you from the elements.
							</motion.p>
							<motion.div 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="mt-6 flex gap-3"
							>
								<motion.a 
									href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" 
									target="_blank" 
									rel="noreferrer"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<Button variant="gradient">Get A Quote!</Button>
								</motion.a>
								<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
									<Link to="/support"><Button variant="outline">Contact Us</Button></Link>
								</motion.div>
							</motion.div>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.8, delay: 0.6 }}
							className="flex justify-center"
						>
							<img src={brokenSideWindow} alt="Broken side window example" className="w-full max-w-lg rounded-lg shadow-lg" />
						</motion.div>
					</div>
				</div>
			</motion.section>

			{/* Installation Process Steps */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h2 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					Our Side Glass Replacement Process
				</motion.h2>
				<motion.div 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
				>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Wrench className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Gain Access</h3>
						<p className="text-gray-600 text-sm">Our technicians begin by carefully removing the door panel, trim pieces, and old glass from the regulator mechanism. This is done using specialized tools to ensure no damage comes to the vehicle's interior, electrical components, or door mechanisms.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.5 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Droplets className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Clean Out Door Panel</h3>
						<p className="text-gray-600 text-sm">The door panel and surrounding areas are thoroughly cleaned to remove any debris, old adhesive, or contaminants that could interfere with the new glass installation. We use specialized cleaning solutions and tools to ensure the mounting surfaces are completely clean.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.6 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Car className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Install Glass</h3>
						<p className="text-gray-600 text-sm">The new side glass is carefully aligned and installed into the vehicle's door frame or quarter panel. Our technicians use precise positioning to ensure a perfect fit, followed by proper mounting to secure the glass in place.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.7 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Settings className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Reassemble Vehicle</h3>
						<p className="text-gray-600 text-sm">All trim pieces, door panels, and electrical connections are carefully reassembled to restore the vehicle to its original condition. Our technicians ensure all components are properly aligned and functioning correctly.</p>
					</motion.div>
				</motion.div>
			</motion.section>

			{/* Peace of Mind Section */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="bg-gray-50 py-12 md:py-16"
			>
				<div className="container">
					<motion.h3 
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-2xl md:text-3xl font-bold text-center mb-8"
					>
						Peace Of Mind
					</motion.h3>
					<div className="max-w-4xl mx-auto text-center">
						<motion.p 
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="text-gray-600 mb-8"
						>
							We provide a lifetime warranty on all our side glass replacement services, ensuring any issues are promptly resolved. To give you further peace of mind, every job and vehicle we service is protected by extensive third-party insurance, offering an added layer of security and trust.
						</motion.p>
						<div className="grid md:grid-cols-3 gap-8">
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<Shield className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Lifetime Warranty</h4>
								<p className="text-gray-600 text-sm">All side glass replacements come with our lifetime warranty protection</p>
							</motion.div>
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<CheckCircle className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Third-Party Insurance</h4>
								<p className="text-gray-600 text-sm">Every job is protected by extensive third-party insurance</p>
							</motion.div>
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.6 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<Settings className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Certified Technicians</h4>
								<p className="text-gray-600 text-sm">Rigorous background verification, training, and certification</p>
							</motion.div>
						</div>
					</div>
				</div>
			</motion.section>

			{/* Types of Side Glass */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h3 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					Types of Side Glass We Replace
				</motion.h3>
				<div className="grid md:grid-cols-3 gap-8">
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Car className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Door Glass</h4>
						<p className="text-gray-600 text-sm">Power-operated windows that roll up and down, providing ventilation and security for your vehicle.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Eye className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Side Windows</h4>
						<p className="text-gray-600 text-sm">Fixed or movable windows on the sides of your vehicle that provide visibility and protection.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.5 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Settings className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Quarter Glass</h4>
						<p className="text-gray-600 text-sm">Located on the sides of your vehicle behind the rear doors, these fixed windows are critical for visibility and aesthetics.</p>
					</motion.div>
				</div>
			</motion.section>

			{/* Signs You Need Replacement */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="bg-gray-50 py-12 md:py-16"
			>
				<div className="container">
					<motion.h3 
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-2xl md:text-3xl font-bold text-center mb-12"
					>
						Signs You Need Side Glass Replacement
					</motion.h3>
					<div className="grid md:grid-cols-2 gap-12">
						<motion.div 
							initial={{ opacity: 0, x: -30 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							<p className="text-gray-600 mb-6">Consider replacing your side glass if you experience:</p>
							<ul className="space-y-3">
								<li className="flex items-start gap-3">
									<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
									<span className="text-gray-600"><strong>Cracks or chips</strong> that compromise visibility or aesthetics.</span>
								</li>
								<li className="flex items-start gap-3">
									<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
									<span className="text-gray-600"><strong>Shattered glass</strong> from an accident, theft, or vandalism.</span>
								</li>
								<li className="flex items-start gap-3">
									<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
									<span className="text-gray-600"><strong>Glass that won't roll up or down properly</strong>, impacting your comfort and security.</span>
								</li>
							</ul>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, x: 30 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.4 }}
						>
							<div className="bg-red-50 border border-red-200 rounded-lg p-6">
								<h4 className="text-lg font-semibold text-red-800 mb-3">Important Safety Note</h4>
								<p className="text-red-700">Driving with damaged side glass isn't just inconvenient—it can also expose your vehicle to theft, weather damage, and reduced structural integrity.</p>
							</div>
						</motion.div>
					</div>
				</div>
			</motion.section>

			{/* Benefits */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h3 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					Benefits of Replacing Your Side Glass
				</motion.h3>
				<div className="grid md:grid-cols-4 gap-8">
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Shield className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Enhanced Safety</h4>
						<p className="text-gray-600 text-sm">Restore the structural integrity of your vehicle</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Settings className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Improved Security</h4>
						<p className="text-gray-600 text-sm">Protect your vehicle and belongings from theft</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.5 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Droplets className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Weather Protection</h4>
						<p className="text-gray-600 text-sm">Keep rain, wind, and debris out of your car</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.9 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.6 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Eye className="w-8 h-8 text-primary" />
						</div>
						<h4 className="text-lg font-semibold mb-3">Aesthetic Appeal</h4>
						<p className="text-gray-600 text-sm">Maintain the sleek, clean look of your vehicle</p>
					</motion.div>
				</div>
			</motion.section>

			{/* Mobile Services */}
			<section className="bg-gray-50 py-12 md:py-16">
				<div className="container text-center">
					<h3 className="text-2xl md:text-3xl font-bold mb-6">Mobile Side Glass Replacement Services</h3>
					<p className="text-gray-600 mb-8 max-w-2xl mx-auto">We understand that a damaged side window can leave you stranded. That's why <strong>Ubiquity AutoGlass Mobile Services</strong> comes to you—whether you're at home, work, or on the road.</p>
				</div>
			</section>

			<section className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white text-center">
					<h3 className="text-2xl font-bold mb-4">Schedule Your Side Glass Replacement Today</h3>
					<p className="mb-6 max-w-2xl mx-auto">Don't let damaged side glass disrupt your safety or peace of mind. Trust <strong>Ubiquity AutoGlass</strong> for fast, reliable, and professional side glass replacement services.</p>
					<div className="flex gap-3 justify-center">
						<a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90">Get A Quote!</Button></a>
						<Link to="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Us</Button></Link>
					</div>
					<p className="mt-6 text-sm opacity-90">Need your side glass replaced? Contact us now!</p>
				</div>
			</section>
		</div>
	);
}
